/**
 * Types pour le système de journaux d'audit (Audit Logs)
 * Correspond à la table activity_logs dans Supabase
 */

export const AuditLogAction = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  LOGIN: 'login',
  LOGOUT: 'logout',
  INVITE: 'invite',
  EXPORT: 'export',
  PUBLISH: 'publish',
  VALIDATE: 'validate',
} as const;

export type AuditLogAction = typeof AuditLogAction[keyof typeof AuditLogAction];

export const AuditLogEntityType = {
  PROJECT: 'project',
  INVOICE: 'invoice',
  USER: 'user',
  CLIENT: 'client',
  TEMPLATE: 'template',
  VIDEO: 'video',
  RENDER: 'render',
  INVITATION: 'invitation',
  PUBLICATION: 'publication',
  MEDIA: 'media',
  SYSTEM: 'system',
  ACTIVATION: 'activation',
  EDITORIAL: 'editorial',
  BUDGET: 'budget',
  ASSIGNMENT: 'assignment',
  EXTERNAL_PROVIDER: 'external_provider',
  TASK: 'task',
  PAYMENT: 'payment',
} as const;

export type AuditLogEntityType = typeof AuditLogEntityType[keyof typeof AuditLogEntityType];

export interface AuditLog {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
  company_id: string | null;
  company_name: string | null;
  action: AuditLogAction;
  entity_type: AuditLogEntityType;
  entity_id: string | null;
  entity_name: string | null;
  details: string | null;
  old_value: Record<string, unknown> | null;
  new_value: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface LogActionInput {
  action: AuditLogAction;
  entity_type: AuditLogEntityType;
  entity_id?: string;
  entity_name?: string;
  details?: string;
  old_value?: Record<string, unknown>;
  new_value?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface AuditLogFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  action?: AuditLogAction;
  entityType?: AuditLogEntityType;
}

// Labels pour affichage
export const ACTION_LABELS: Record<AuditLogAction, string> = {
  [AuditLogAction.CREATE]: 'Création',
  [AuditLogAction.UPDATE]: 'Modification',
  [AuditLogAction.DELETE]: 'Suppression',
  [AuditLogAction.LOGIN]: 'Connexion',
  [AuditLogAction.LOGOUT]: 'Déconnexion',
  [AuditLogAction.INVITE]: 'Invitation',
  [AuditLogAction.EXPORT]: 'Export',
  [AuditLogAction.PUBLISH]: 'Publication',
  [AuditLogAction.VALIDATE]: 'Validation',
};

export const ENTITY_TYPE_LABELS: Record<AuditLogEntityType, string> = {
  [AuditLogEntityType.PROJECT]: 'Projet',
  [AuditLogEntityType.INVOICE]: 'Facture',
  [AuditLogEntityType.USER]: 'Utilisateur',
  [AuditLogEntityType.CLIENT]: 'Client',
  [AuditLogEntityType.TEMPLATE]: 'Template',
  [AuditLogEntityType.VIDEO]: 'Vidéo',
  [AuditLogEntityType.RENDER]: 'Rendu',
  [AuditLogEntityType.INVITATION]: 'Invitation',
  [AuditLogEntityType.PUBLICATION]: 'Publication',
  [AuditLogEntityType.MEDIA]: 'Média',
  [AuditLogEntityType.SYSTEM]: 'Système',
  [AuditLogEntityType.ACTIVATION]: 'Activation',
  [AuditLogEntityType.EDITORIAL]: 'Calendrier éditorial',
  [AuditLogEntityType.BUDGET]: 'Budget',
  [AuditLogEntityType.ASSIGNMENT]: 'Assignation',
  [AuditLogEntityType.EXTERNAL_PROVIDER]: 'Prestataire externe',
  [AuditLogEntityType.TASK]: 'Tâche',
  [AuditLogEntityType.PAYMENT]: 'Paiement',
};

// Icônes visuelles pour les actions
export const ACTION_ICONS: Record<AuditLogAction, string> = {
  [AuditLogAction.CREATE]: '🟢',
  [AuditLogAction.UPDATE]: '🔵',
  [AuditLogAction.DELETE]: '🔴',
  [AuditLogAction.LOGIN]: '🔑',
  [AuditLogAction.LOGOUT]: '🚪',
  [AuditLogAction.INVITE]: '📧',
  [AuditLogAction.EXPORT]: '📦',
  [AuditLogAction.PUBLISH]: '📤',
  [AuditLogAction.VALIDATE]: '💰',
};

// Couleurs pour les badges d'action
export const ACTION_COLORS: Record<AuditLogAction, string> = {
  [AuditLogAction.CREATE]: 'bg-green-100 text-green-700',
  [AuditLogAction.UPDATE]: 'bg-blue-100 text-blue-700',
  [AuditLogAction.DELETE]: 'bg-red-100 text-red-700',
  [AuditLogAction.LOGIN]: 'bg-purple-100 text-purple-700',
  [AuditLogAction.LOGOUT]: 'bg-gray-100 text-gray-700',
  [AuditLogAction.INVITE]: 'bg-indigo-100 text-indigo-700',
  [AuditLogAction.EXPORT]: 'bg-yellow-100 text-yellow-700',
  [AuditLogAction.PUBLISH]: 'bg-pink-100 text-pink-700',
  [AuditLogAction.VALIDATE]: 'bg-orange-100 text-orange-700',
};
